import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import { getAuth } from '../auth/auth';
import {
  BookOpen,
  Filter,
  Plus,
  RefreshCw,
  Printer,
  X,
  Flag,
  Radio,
  Send,
  ArrowDownLeft,
  ArrowUpRight,
  ShieldAlert,
  Clock,
  Search,
  CheckCircle,
  FileText,
  User,
  AlertTriangle,
  Layers,
  Save,
  Edit3,
  MessageSquare,
} from 'lucide-react';

export interface EtbEntry {
  id: number;
  entry_number: number;
  timestamp: string;
  channel: string;
  sender?: string;
  recipient?: string;
  direction: 'INCOMING' | 'OUTGOING' | 'INTERNAL';
  priority: 'NORMAL' | 'SOFORT' | 'BLITZ';
  subject?: string;
  content: string;
  section?: string;
  category: string;
  status: 'ERFASST' | 'IN_BEARBEITUNG' | 'ERLEDIGT' | 'WEITERGELEITET';
  handled_by_name?: string;
  handled_at?: string;
  created_by_name?: string;
  mission_number?: string;
  operation_name?: string;
  operation_number?: string;
  metadata?: {
    vermerk?: string;
    comments?: Array<{
      id: string;
      text: string;
      author: string;
      created_at: string;
    }>;
    [key: string]: any;
  };
}

export interface DispatchOperation {
  id: number;
  operation_number: string;
  name: string;
  type: string;
  status: string;
  start_time: string;
  location_address?: string;
}

export interface OperationSection {
  id: number;
  operation_id: number;
  name: string;
  section_code?: string;
  leader_name?: string;
  radio_channel?: string;
  description?: string;
  color?: string;
}

const SECTION_OPTIONS = [
  { code: 'S1', label: 'S1 – Personal / Innerer Dienst' },
  { code: 'S2', label: 'S2 – Lage' },
  { code: 'S3', label: 'S3 – Einsatz / Führung' },
  { code: 'S4', label: 'S4 – Versorgung / Logistik' },
  { code: 'S5', label: 'S5 – Presse & Medien' },
  { code: 'S6', label: 'S6 – IT & Kommunikation' },
  { code: 'GESAMTLEITUNG', label: 'Gesamtleitung / Stab' },
];

export default function EtbPage() {
  const [operations, setOperations] = useState<DispatchOperation[]>([]);
  const [selectedOperationId, setSelectedOperationId] = useState<number | null>(null);
  const [entries, setEntries] = useState<EtbEntry[]>([]);
  const [sections, setSections] = useState<OperationSection[]>([]);
  const [loading, setLoading] = useState(false);

  // Filter
  const [filterSection, setFilterSection] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showOpModal, setShowOpModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showVordruckModal, setShowVordruckModal] = useState(false);
  const [selectedVordruckEntry, setSelectedVordruckEntry] = useState<EtbEntry | null>(null);
  const [activeExemplar, setActiveExemplar] = useState<1 | 2 | 3 | 4>(1);

  // Vordruck Edit State
  const [isVordruckEditing, setIsVordruckEditing] = useState(false);
  const [vordruckSaving, setVordruckSaving] = useState(false);
  const [editDirection, setEditDirection] = useState<'INCOMING' | 'OUTGOING' | 'INTERNAL'>('INCOMING');
  const [editChannel, setEditChannel] = useState('FUNK');
  const [editSender, setEditSender] = useState('');
  const [editRecipient, setEditRecipient] = useState('');
  const [editPriority, setEditPriority] = useState<'NORMAL' | 'SOFORT' | 'BLITZ'>('NORMAL');
  const [editSection, setEditSection] = useState('S2');
  const [editSubject, setEditSubject] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editStatus, setEditStatus] = useState<any>('ERFASST');
  const [editVermerk, setEditVermerk] = useState('');

  // Kommentar State
  const [newCommentText, setNewCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // PDF Generator State
  const [pdfLoading, setPdfLoading] = useState(false);

  // Neuer Eintrag
  const [newDirection, setNewDirection] = useState<'INCOMING' | 'OUTGOING' | 'INTERNAL'>('INCOMING');
  const [newChannel, setNewChannel] = useState('FUNK');
  const [newSender, setNewSender] = useState('');
  const [newRecipient, setNewRecipient] = useState('');
  const [newPriority, setNewPriority] = useState<'NORMAL' | 'SOFORT' | 'BLITZ'>('NORMAL');
  const [newSection, setNewSection] = useState('S2');
  const [newSubject, setNewSubject] = useState('');
  const [newContent, setNewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Neue Lage
  const [newOpName, setNewOpName] = useState('');
  const [newOpType, setNewOpType] = useState('LAGE');
  const [newOpLoc, setNewOpLoc] = useState('');
  const [newOpDesc, setNewOpDesc] = useState('');
  const [savingOp, setSavingOp] = useState(false);

  // Neuer Abschnitt
  const [newSecCode, setNewSecCode] = useState('EA 1');
  const [newSecName, setNewSecName] = useState('');
  const [newSecLeader, setNewSecLeader] = useState('');
  const [newSecRadio, setNewSecRadio] = useState('');
  const [newSecColor, setNewSecColor] = useState('#3b82f6');
  const [savingSec, setSavingSec] = useState(false);

  // Lagen laden
  const loadOperations = async () => {
    try {
      const data = await apiFetch('/etb/operations');
      const ops = Array.isArray(data) ? data : [];
      setOperations(ops);
      // Wenn Lagen existieren und noch keine gewählt, die erste aktive oder die erste Lage auswählen
      if (ops.length > 0 && selectedOperationId === null) {
        const active = ops.find((o: any) => o.status === 'ACTIVE') || ops[0];
        setSelectedOperationId(active.id);
      }
    } catch (e) {
      console.error('Fehler beim Laden der Lagen:', e);
    }
  };

  // Abschnitte laden
  const loadSections = async () => {
    if (!selectedOperationId) {
      setSections([]);
      return;
    }
    try {
      const data = await apiFetch(`/etb/sections/${selectedOperationId}`);
      setSections(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Fehler beim Laden der Abschnitte:', e);
    }
  };

  // Einträge laden
  const loadEntries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedOperationId !== null) {
        params.set('operation_id', String(selectedOperationId));
      } else {
        params.set('operation_id', '0');
      }
      if (filterSection) params.set('section', filterSection);
      if (filterStatus) params.set('status', filterStatus);
      if (searchTerm) params.set('search', searchTerm);

      const data = await apiFetch(`/etb/entries?${params.toString()}`);
      setEntries(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Fehler beim Laden der ETB-Einträge:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOperations();
  }, []);

  useEffect(() => {
    loadSections();
    loadEntries();
    const timer = setInterval(() => {
      loadEntries();
    }, 10000);
    return () => clearInterval(timer);
  }, [selectedOperationId, filterSection, filterStatus, searchTerm]);

  // Eintrag speichern
  const handleCreateEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContent.trim()) return;
    try {
      setSubmitting(true);
      await apiFetch('/etb/entries', {
        method: 'POST',
        body: JSON.stringify({
          operation_id: selectedOperationId,
          direction: newDirection,
          channel: newChannel,
          sender: newSender.trim() || undefined,
          recipient: newRecipient.trim() || undefined,
          priority: newPriority,
          section: newSection || undefined,
          subject: newSubject.trim() || undefined,
          content: newContent.trim(),
        }),
      });
      setShowAddModal(false);
      setNewSubject('');
      setNewContent('');
      setNewSender('');
      setNewRecipient('');
      await loadEntries();
    } catch (err: any) {
      alert(`Fehler beim Speichern: ${err?.message || 'Unbekannt'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Status ändern
  const handleUpdateStatus = async (id: number, status: 'IN_BEARBEITUNG' | 'ERLEDIGT') => {
    try {
      await apiFetch(`/etb/entries/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      await loadEntries();
    } catch (err) {
      console.error(err);
    }
  };

  // Vordruck-Bearbeitung initialisieren
  const initVordruckEdit = (entry: EtbEntry) => {
    setEditDirection(entry.direction || 'INCOMING');
    setEditChannel(entry.channel || 'FUNK');
    setEditSender(entry.sender || '');
    setEditRecipient(entry.recipient || '');
    setEditPriority(entry.priority || 'NORMAL');
    setEditSection(entry.section || 'S2');
    setEditSubject(entry.subject || '');
    setEditContent(entry.content || '');
    setEditStatus(entry.status || 'ERFASST');
    setEditVermerk(entry.metadata?.vermerk || '');
    setNewCommentText('');
  };

  // Vordruck speichern (PATCH /api/dispatch/etb/:id)
  const handleSaveVordruck = async () => {
    if (!selectedVordruckEntry) return;
    try {
      setVordruckSaving(true);
      const auth = getAuth();
      const token = auth?.token;
      const res = await fetch(`/api/dispatch/etb/${selectedVordruckEntry.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          direction: editDirection,
          channel: editChannel,
          sender: editSender,
          recipient: editRecipient,
          priority: editPriority,
          section: editSection,
          subject: editSubject,
          content: editContent,
          status: editStatus,
          vermerk: editVermerk,
        }),
      });

      if (res.ok) {
        const updated = await res.json();
        setSelectedVordruckEntry(prev => (prev ? { ...prev, ...updated } : updated));
        setEntries(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
        setIsVordruckEditing(false);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.message || 'Fehler beim Speichern des Vordrucks');
      }
    } catch (e: any) {
      alert(e.message || 'Netzwerkfehler');
    } finally {
      setVordruckSaving(false);
    }
  };

  // Kommentar hinzufügen
  const handleAddComment = async (entryId: number) => {
    if (!newCommentText.trim()) return;
    try {
      setCommentSubmitting(true);
      const auth = getAuth();
      const token = auth?.token;
      const res = await fetch(`/api/dispatch/etb/${entryId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text: newCommentText.trim() }),
      });

      if (res.ok) {
        const updated = await res.json();
        setSelectedVordruckEntry(prev => (prev ? { ...prev, metadata: updated.metadata } : updated));
        setEntries(prev => prev.map(e => e.id === updated.id ? { ...e, metadata: updated.metadata } : e));
        setNewCommentText('');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.message || 'Fehler beim Hinzufügen des Kommentars');
      }
    } catch (e: any) {
      alert(e.message || 'Netzwerkfehler');
    } finally {
      setCommentSubmitting(false);
    }
  };

  // PDF Generierung (digitaler behördlicher DIN-A4 Nachrichtenvordruck Typ B)
  const handleOpenPdf = async (entryId: number, exemplar: number | 'all' = 1) => {
    try {
      setPdfLoading(true);
      const auth = getAuth();
      const token = auth?.token;
      const res = await fetch(`/api/dispatch/etb/${entryId}/pdf?exemplar=${exemplar}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        throw new Error('PDF konnte nicht erstellt werden');
      }
      const blob = await res.blob();
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, '_blank');
    } catch (e: any) {
      alert(e.message || 'Fehler beim Öffnen des PDFs');
    } finally {
      setPdfLoading(false);
    }
  };

  // Existierende Einträge je Lage chronologisch neu nummerieren
  const handleRenumber = async () => {
    try {
      await apiFetch('/etb/renumber', { method: 'POST' });
      await loadEntries();
    } catch (err) {
      console.error('Fehler beim Gradeziehen der Nummern:', err);
    }
  };

  // Neue Lage anlegen
  const handleCreateOperation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOpName.trim()) return;
    try {
      setSavingOp(true);
      const created = await apiFetch('/etb/operations', {
        method: 'POST',
        body: JSON.stringify({
          name: newOpName.trim(),
          type: newOpType,
          location_address: newOpLoc.trim() || undefined,
          description: newOpDesc.trim() || undefined,
        }),
      });
      setShowOpModal(false);
      setNewOpName('');
      setNewOpLoc('');
      setNewOpDesc('');
      await loadOperations();
      if (created?.id) setSelectedOperationId(created.id);
    } catch (err: any) {
      alert(`Fehler beim Anlegen der Lage: ${err?.message || 'Unbekannt'}`);
    } finally {
      setSavingOp(false);
    }
  };

  // Neuen Abschnitt anlegen
  const handleCreateSection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOperationId || !newSecName.trim()) return;
    try {
      setSavingSec(true);
      await apiFetch(`/etb/sections/${selectedOperationId}`, {
        method: 'POST',
        body: JSON.stringify({
          name: newSecName.trim(),
          section_code: newSecCode.trim() || undefined,
          leader_name: newSecLeader.trim() || undefined,
          radio_channel: newSecRadio.trim() || undefined,
          color: newSecColor,
        }),
      });
      setNewSecName('');
      setNewSecCode('');
      setNewSecLeader('');
      setNewSecRadio('');
      await loadSections();
    } catch (err: any) {
      alert(`Fehler beim Speichern des Abschnitts: ${err?.message || 'Unbekannt'}`);
    } finally {
      setSavingSec(false);
    }
  };

  // Abschnitt löschen
  const handleDeleteSection = async (id: number) => {
    if (!confirm('Einsatzabschnitt wirklich löschen?')) return;
    try {
      await apiFetch(`/etb/sections/${id}`, { method: 'DELETE' });
      await loadSections();
    } catch (err) {
      console.error(err);
    }
  };

  const activeOp = operations.find((o) => o.id === selectedOperationId);

  return (
    <div className="space-y-4 font-sans text-gray-900 dark:text-slate-100">
      {/* Top Banner / Navigation Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-800 p-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900/60 shadow-sm">
              <BookOpen className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                  EINSATZTAGEBUCH <span className="text-blue-600 dark:text-blue-400">(ETB)</span>
                </h1>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                  Stabsarbeit & DV 100
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                Revisionssichere Dokumentation · 1:1 Live-Spiegelung mit dem Einsatzleitsystem · Digitaler 4-fach Meldevordruck
              </p>
            </div>
          </div>

          {/* Lagen-Umschalter & Hauptaktionen */}
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-slate-950 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-slate-800">
              <span className="text-xs font-semibold text-gray-500 dark:text-slate-400">Aktive Lage:</span>
              <select
                value={selectedOperationId !== null ? selectedOperationId : '0'}
                onChange={(e) => setSelectedOperationId(e.target.value === '0' ? null : parseInt(e.target.value, 10))}
                className="bg-transparent text-xs font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer"
              >
                <option value="0">🏢 Regelbetrieb (Leitstelle)</option>
                {operations.map((op) => (
                  <option key={op.id} value={op.id}>
                    [{op.operation_number}] {op.name} ({op.status})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => setShowOpModal(true)}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border border-gray-200 dark:border-slate-700"
              title="Neue Lage oder Sanitätsdienst anlegen"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Neue Lage</span>
            </button>

            {selectedOperationId && (
              <button
                onClick={() => setShowSectionModal(true)}
                className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border border-indigo-200 dark:border-indigo-800"
              >
                <Flag className="w-3.5 h-3.5" />
                <span>Abschnitte ({sections.length})</span>
              </button>
            )}

            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-xs font-bold shadow-md transition flex items-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Neuer Eintrag</span>
            </button>

            <button
              onClick={() => {
                handleRenumber();
                loadEntries();
                loadSections();
              }}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white bg-gray-100 dark:bg-slate-800 rounded-xl transition border border-gray-200 dark:border-slate-700"
              title="Aktualisieren & Nummern synchronisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filter-Leiste */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5 pt-4 mt-4 border-t border-gray-100 dark:border-slate-800 text-xs">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Suche (Text, Sender, Empfänger)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={filterSection}
            onChange={(e) => setFilterSection(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 font-medium"
          >
            <option value="">Alle Abschnitte</option>
            {sections.length > 0 && (
              <optgroup label="🚩 Einsatzabschnitte (Lage)">
                {sections.map((s) => (
                  <option key={`sec-${s.id}`} value={s.section_code || s.name}>
                    {s.section_code ? `${s.section_code} – ` : ''}{s.name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="🏛️ Stabsfunktionen (DV 100)">
              {SECTION_OPTIONS.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </optgroup>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 font-medium"
          >
            <option value="">Alle Status</option>
            <option value="ERFASST">Erfasst</option>
            <option value="IN_BEARBEITUNG">In Bearbeitung</option>
            <option value="ERLEDIGT">Erledigt</option>
          </select>

          <div className="flex items-center justify-end text-xs text-gray-500 dark:text-slate-400 font-medium">
            <span>{entries.length} Einträge im Tagebuch</span>
          </div>
        </div>
      </div>

      {/* ETB Tabellenliste */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden">
        {loading && entries.length === 0 ? (
          <div className="py-16 text-center text-xs text-gray-400">Lade Tagebucheinträge…</div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center text-xs text-gray-400 italic">
            Keine Einträge für diesen Kontext vorhanden.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50/80 dark:bg-slate-950/80 border-b border-gray-200 dark:border-slate-800 text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  <th className="p-3 w-16">Nr.</th>
                  <th className="p-3 w-24">Uhrzeit</th>
                  <th className="p-3 w-24">Kanal</th>
                  <th className="p-3 w-44">Von / An</th>
                  <th className="p-3 w-28">Abschnitt</th>
                  <th className="p-3">Meldung / Inhalt</th>
                  <th className="p-3 w-32">Status</th>
                  <th className="p-3 w-32 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800/60">
                {entries.map((entry) => {
                  const isBlitz = entry.priority === 'BLITZ';
                  const isSofort = entry.priority === 'SOFORT';
                  const timeFormatted = new Date(entry.timestamp).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  });
                  const dateFormatted = new Date(entry.timestamp).toLocaleDateString('de-DE');

                  return (
                    <tr
                      key={entry.id}
                      className={`transition duration-150 ${
                        isBlitz
                          ? 'bg-rose-50/60 dark:bg-rose-950/30 border-l-4 border-l-rose-500'
                          : isSofort
                          ? 'bg-amber-50/60 dark:bg-amber-950/30 border-l-4 border-l-amber-500'
                          : entry.status === 'ERLEDIGT'
                          ? 'bg-emerald-50/30 dark:bg-emerald-950/10 border-l-4 border-l-emerald-500'
                          : 'hover:bg-blue-50/40 dark:hover:bg-slate-800/40 border-l-4 border-l-transparent'
                      }`}
                    >
                      <td className="p-3 font-mono font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                        {entry.operation_number ? `${entry.operation_number}-#${entry.entry_number}` : `#${entry.entry_number}`}
                      </td>
                      <td className="p-3 font-mono whitespace-nowrap">
                        <div className="font-bold text-gray-900 dark:text-white">{timeFormatted}</div>
                        <div className="text-[10px] text-gray-400">{dateFormatted}</div>
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            entry.channel === 'FUNK'
                              ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                              : entry.channel === 'TELEFON'
                              ? 'bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-800'
                              : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-700'
                          }`}
                        >
                          {entry.channel}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {entry.sender || '—'}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                          <span className="text-gray-400">➜</span> {entry.recipient || '—'}
                        </div>
                      </td>
                      <td className="p-3 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                        {entry.section || '—'}
                      </td>
                      <td className="p-3">
                        {entry.subject && (
                          <div className="font-bold text-blue-600 dark:text-blue-400 mb-0.5">
                            {entry.subject}
                          </div>
                        )}
                        <div className="text-gray-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                          {entry.content}
                        </div>
                        {entry.created_by_name && (
                          <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span>Erfasst von {entry.created_by_name}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            entry.status === 'ERLEDIGT'
                              ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                              : entry.status === 'IN_BEARBEITUNG'
                              ? 'bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800'
                              : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-700'
                          }`}
                        >
                          {entry.status}
                        </span>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => {
                              setSelectedVordruckEntry(entry);
                              initVordruckEdit(entry);
                              setIsVordruckEditing(false);
                              setActiveExemplar(1);
                              setShowVordruckModal(true);
                            }}
                            className="px-2 py-1 rounded bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-[11px] font-bold transition"
                            title="4-fach Meldevordruck (Typ B) anzeigen"
                          >
                            📄 4-fach
                          </button>
                          <button
                            onClick={() => {
                              setSelectedVordruckEntry(entry);
                              initVordruckEdit(entry);
                              setIsVordruckEditing(true);
                              setActiveExemplar(1);
                              setShowVordruckModal(true);
                            }}
                            className="px-2 py-1 rounded bg-sky-50 hover:bg-sky-100 dark:bg-sky-950/50 dark:hover:bg-sky-900/60 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-800 text-[11px] font-bold transition flex items-center gap-1"
                            title="Vordruck / Eintrag bearbeiten"
                          >
                            <span>✏️ Bearbeiten</span>
                          </button>
                          <button
                            onClick={() => handleOpenPdf(entry.id, 1)}
                            disabled={pdfLoading}
                            className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 text-[11px] font-bold transition flex items-center gap-1"
                            title="Als druckfertiges DIN-A4 Behörden-PDF öffnen"
                          >
                            <span>🖨️ PDF</span>
                          </button>
                          {entry.status === 'ERFASST' && (
                            <button
                              onClick={() => handleUpdateStatus(entry.id, 'IN_BEARBEITUNG')}
                              className="px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-[11px] font-bold transition"
                            >
                              Bearb.
                            </button>
                          )}
                          {entry.status === 'IN_BEARBEITUNG' && (
                            <button
                              onClick={() => handleUpdateStatus(entry.id, 'ERLEDIGT')}
                              className="px-2 py-1 rounded bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-[11px] font-bold transition"
                            >
                              Erledigt
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Neuer ETB-Eintrag */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 w-full max-w-lg rounded-2xl shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-800 pb-3">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span>📝</span> Neuer ETB-Eintrag
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateEntry} className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                    Richtung
                  </label>
                  <select
                    value={newDirection}
                    onChange={(e) => setNewDirection(e.target.value as any)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                  >
                    <option value="INCOMING">Eingang (Empfangen)</option>
                    <option value="OUTGOING">Ausgang (Gesendet)</option>
                    <option value="INTERNAL">Intern (Vermerk/Befehl)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                    Übermittlungsweg
                  </label>
                  <select
                    value={newChannel}
                    onChange={(e) => setNewChannel(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                  >
                    <option value="FUNK">Funk (4m / 2m / Tetra)</option>
                    <option value="TELEFON">Telefon / Leitstelle</option>
                    <option value="DIGITAL">Digital (Web/App)</option>
                    <option value="BOTEN">Melder / Bote</option>
                    <option value="PERSOENLICH">Persönlich / Mündlich</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                    Von (Absender)
                  </label>
                  <input
                    type="text"
                    placeholder="z.B. ELW 1, Leitstelle..."
                    value={newSender}
                    onChange={(e) => setNewSender(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                    An (Empfänger)
                  </label>
                  <input
                    type="text"
                    placeholder="z.B. Stab, EA 1, RTW 1..."
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                    Abschnitt / Zuständigkeit *
                  </label>
                  <select
                    value={newSection}
                    onChange={(e) => setNewSection(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                  >
                    {sections.length > 0 && (
                      <optgroup label="🚩 Einsatzabschnitte (Lage)">
                        {sections.map((s) => (
                          <option key={`opt-${s.id}`} value={s.section_code || s.name}>
                            {s.section_code ? `${s.section_code} – ` : ''}{s.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label="🏛️ Stabsfunktionen (DV 100)">
                      {SECTION_OPTIONS.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                    Priorität
                  </label>
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value as any)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                  >
                    <option value="NORMAL">Normal</option>
                    <option value="SOFORT">Sofort</option>
                    <option value="BLITZ">Blitz (Höchste Eile)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                  Betreff / Stichwort
                </label>
                <input
                  type="text"
                  placeholder="z.B. Lagemeldung EA 1, Nachforderung RTW..."
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                  Inhalt / Wortlaut der Meldung *
                </label>
                <textarea
                  rows={4}
                  required
                  placeholder="Vollständiger Wortlaut der Meldung oder Anweisung..."
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white resize-y"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={submitting || !newContent.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition shadow"
                >
                  {submitting ? 'Speichert…' : 'Eintrag erfassen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Neue Lage anlegen */}
      {showOpModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 w-full max-w-md rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-800 pb-3">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span>🏛️</span> Neue Lage / Einsatz anlegen
              </h2>
              <button
                onClick={() => setShowOpModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateOperation} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                  Name / Bezeichnung der Lage *
                </label>
                <input
                  type="text"
                  required
                  placeholder="z.B. Großbrand Hafen, Marathon 2026..."
                  value={newOpName}
                  onChange={(e) => setNewOpName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                  Typ
                </label>
                <select
                  value={newOpType}
                  onChange={(e) => setNewOpType(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                >
                  <option value="LAGE">⚠️ Großlage</option>
                  <option value="SANITAETSDIENST">🚑 Sanitätsdienst</option>
                  <option value="UEBUNG">🎯 Übung</option>
                  <option value="SONDERLAGE">🚨 Sonderlage</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 mb-1">
                  Einsatzort / Adresse
                </label>
                <input
                  type="text"
                  placeholder="z.B. Landungsbrücken, Hamburg..."
                  value={newOpLoc}
                  onChange={(e) => setNewOpLoc(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowOpModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={savingOp || !newOpName.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition shadow"
                >
                  {savingOp ? 'Erstellt…' : 'Lage anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Einsatzabschnitte verwalten */}
      {showSectionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 w-full max-w-lg rounded-2xl shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-800 pb-3">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span>🚩</span> Einsatzabschnitte verwalten
              </h2>
              <button
                onClick={() => setShowSectionModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white font-bold"
              >
                ✕
              </button>
            </div>

            {/* Neuer Abschnitt Formular */}
            <form onSubmit={handleCreateSection} className="p-4 bg-gray-50 dark:bg-slate-950 rounded-xl border border-gray-200 dark:border-slate-800 space-y-3">
              <div className="text-xs font-bold text-blue-600 dark:text-blue-400">
                ➕ Neuen Einsatzabschnitt hinzufügen
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 dark:text-slate-400 mb-1">
                    Kürzel
                  </label>
                  <input
                    type="text"
                    placeholder="EA 1"
                    value={newSecCode}
                    onChange={(e) => setNewSecCode(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-lg text-xs"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-500 dark:text-slate-400 mb-1">
                    Abschnittsname *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="z.B. Brandbekämpfung, Patiententransport..."
                    value={newSecName}
                    onChange={(e) => setNewSecName(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-lg text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 dark:text-slate-400 mb-1">
                    Leiter / Verantwortlicher
                  </label>
                  <input
                    type="text"
                    placeholder="z.B. BM Müller"
                    value={newSecLeader}
                    onChange={(e) => setNewSecLeader(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 dark:text-slate-400 mb-1">
                    Funkkanal / Rufgruppe
                  </label>
                  <input
                    type="text"
                    placeholder="z.B. DMO 310"
                    value={newSecRadio}
                    onChange={(e) => setNewSecRadio(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-lg text-xs"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="submit"
                  disabled={savingSec || !newSecName.trim()}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition shadow"
                >
                  {savingSec ? 'Speichert…' : 'Abschnitt anlegen'}
                </button>
              </div>
            </form>

            {/* Bestehende Abschnitte */}
            <div className="space-y-2">
              <div className="text-xs font-bold text-gray-600 dark:text-slate-400 uppercase tracking-wider">
                Bestehende Abschnitte ({sections.length})
              </div>
              {sections.length === 0 ? (
                <div className="p-4 text-center text-xs text-gray-400 italic">
                  Keine Abschnitte für diese Lage definiert.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {sections.map((s) => (
                    <div
                      key={s.id}
                      className="p-2.5 rounded-xl bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 flex items-center justify-between text-xs"
                    >
                      <div>
                        <div className="font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                          <span className="font-mono text-blue-600 dark:text-blue-400">
                            [{s.section_code || 'EA'}]
                          </span>
                          <span>{s.name}</span>
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">
                          {s.leader_name ? `Leiter: ${s.leader_name} ` : ''}
                          {s.radio_channel ? `· Funk: ${s.radio_channel}` : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteSection(s.id)}
                        className="text-rose-500 hover:text-rose-700 font-bold p-1"
                        title="Löschen"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-3 border-t border-gray-200 dark:border-slate-800">
              <button
                onClick={() => setShowSectionModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 text-gray-700 dark:text-slate-200 text-xs font-semibold rounded-xl"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Digitaler 4-fach Meldevordruck (Typ B) nach Behördenstandard */}
      {showVordruckModal && selectedVordruckEntry && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-950 border border-slate-800 text-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
            {/* Header Toolbar */}
            <div className="bg-slate-900 border-b border-slate-800 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">📋</span>
                <div>
                  <h3 className="text-sm font-bold font-mono tracking-wide text-white">
                    4-FACH MELDEVORDRUCK (TYP B)
                  </h3>
                  <div className="text-[11px] text-gray-400">
                    ETB #{String(selectedVordruckEntry.entry_number).padStart(4, '0')} · {selectedVordruckEntry.direction === 'INCOMING' ? 'Eingang' : selectedVordruckEntry.direction === 'OUTGOING' ? 'Ausgang' : 'Intern'}
                  </div>
                </div>
              </div>

              {/* Durchschlag-Umschalter */}
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setActiveExemplar(1)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition ${
                    activeExemplar === 1
                      ? 'bg-white text-gray-900 border-white shadow'
                      : 'bg-slate-800 text-gray-300 border-slate-700 hover:bg-slate-700'
                  }`}
                >
                  1. Weiß (Empfänger)
                </button>
                <button
                  type="button"
                  onClick={() => setActiveExemplar(2)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition ${
                    activeExemplar === 2
                      ? 'bg-amber-100 text-amber-900 border-amber-300 shadow'
                      : 'bg-slate-800 text-gray-300 border-slate-700 hover:bg-slate-700'
                  }`}
                >
                  2. Gelb (Absender)
                </button>
                <button
                  type="button"
                  onClick={() => setActiveExemplar(3)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition ${
                    activeExemplar === 3
                      ? 'bg-emerald-100 text-emerald-900 border-emerald-300 shadow'
                      : 'bg-slate-800 text-gray-300 border-slate-700 hover:bg-slate-700'
                  }`}
                >
                  3. Grün (Fernmeldebetr.)
                </button>
                <button
                  type="button"
                  onClick={() => setActiveExemplar(4)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition ${
                    activeExemplar === 4
                      ? 'bg-pink-100 text-pink-900 border-pink-300 shadow'
                      : 'bg-slate-800 text-gray-300 border-slate-700 hover:bg-slate-700'
                  }`}
                >
                  4. Rosa (Tagebuch)
                </button>
              </div>

              {/* Aktionen im Toolbar */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!isVordruckEditing) {
                      initVordruckEdit(selectedVordruckEntry);
                    }
                    setIsVordruckEditing(!isVordruckEditing);
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-bold border transition flex items-center gap-1.5 ${
                    isVordruckEditing
                      ? 'bg-amber-900/60 border-amber-500 text-amber-200 hover:bg-amber-900'
                      : 'bg-sky-900/60 border-sky-500 text-sky-200 hover:bg-sky-900'
                  }`}
                >
                  <span>{isVordruckEditing ? '👁️ Nur Leseansicht' : '✏️ Bearbeiten'}</span>
                </button>

                {isVordruckEditing && (
                  <button
                    type="button"
                    onClick={handleSaveVordruck}
                    disabled={vordruckSaving}
                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold shadow flex items-center gap-1.5 transition disabled:opacity-50"
                  >
                    <Save className="w-3.5 h-3.5" />
                    <span>{vordruckSaving ? 'Speichern…' : 'Speichern'}</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleOpenPdf(selectedVordruckEntry.id, activeExemplar)}
                  disabled={pdfLoading}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold shadow flex items-center gap-1.5 transition disabled:opacity-50"
                  title="Dieses Exemplar als druckfertiges DIN-A4 PDF öffnen"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>{pdfLoading ? 'PDF wird erstellt…' : 'Als PDF drucken'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenPdf(selectedVordruckEntry.id, 'all')}
                  disabled={pdfLoading}
                  className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold shadow flex items-center gap-1.5 transition disabled:opacity-50"
                  title="Kompletten 4-fach Satz (alle 4 Seiten) als DIN-A4 PDF generieren"
                >
                  <span>📑 4-fach Satz (PDF)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowVordruckModal(false)}
                  className="text-gray-400 hover:text-white font-bold text-lg ml-2"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Vordruck Body Formular */}
            <div
              className={`p-6 overflow-y-auto flex-1 font-sans text-gray-900 transition-colors ${
                activeExemplar === 1
                  ? 'bg-white'
                  : activeExemplar === 2
                  ? 'bg-[#fefce8]'
                  : activeExemplar === 3
                  ? 'bg-[#f0fdf4]'
                  : 'bg-[#fdf2f8]'
              }`}
            >
              <div className="border-2 border-black p-1 bg-transparent text-xs leading-snug max-w-3xl mx-auto shadow-sm">
                {/* Titelzeile */}
                <div className="grid grid-cols-3 border-b-2 border-black p-2 bg-black/5 items-center">
                  <div>
                    <div className="font-bold text-sm tracking-tight">NACHRICHTENVORDRUCK</div>
                    <div className="text-[9px] text-gray-600 uppercase">nach DV 100 / PDV 810 (Typ B)</div>
                  </div>
                  <div className="text-center font-bold text-xs uppercase tracking-wide">
                    {activeExemplar === 1 && '1. AUSFERTIGUNG – EMPFÄNGER (WEISS)'}
                    {activeExemplar === 2 && '2. AUSFERTIGUNG – ABSENDER (GELB)'}
                    {activeExemplar === 3 && '3. AUSFERTIGUNG – FERNMELDEBETRIEBSSTELLE (GRÜN)'}
                    {activeExemplar === 4 && '4. AUSFERTIGUNG – TAGEBUCHFÜHRUNG (ROSA)'}
                  </div>
                  <div className="text-right font-mono font-bold text-base text-blue-900">
                    ETB-Nr.: #{String(selectedVordruckEntry.entry_number).padStart(4, '0')}
                  </div>
                </div>

                {/* Zeile 1: Beförderungsweg | Vorrangstufe | Richtung */}
                <div className="grid grid-cols-3 border-b border-black">
                  <div className="p-2 border-r border-black col-span-1">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">
                      Beförderungsweg / Rufname Gegenstelle
                    </div>
                    {isVordruckEditing ? (
                      <div className="flex gap-1 mt-1">
                        <select
                          value={editChannel}
                          onChange={e => setEditChannel(e.target.value)}
                          className="border border-black text-xs p-1 font-bold bg-white text-black"
                        >
                          <option value="FUNK">Funk</option>
                          <option value="TELEFON">Telefon</option>
                          <option value="DIGITAL">Digital</option>
                          <option value="BOTEN">Melder/Bote</option>
                          <option value="PERSOENLICH">Persönlich/Mündlich</option>
                        </select>
                        <input
                          type="text"
                          value={editRecipient}
                          onChange={e => setEditRecipient(e.target.value)}
                          placeholder="Rufname Gegenstelle"
                          className="border border-black text-xs p-1 flex-1 font-bold bg-white text-black"
                        />
                      </div>
                    ) : (
                      <div className="font-bold text-sm mt-1">
                        {selectedVordruckEntry.channel}{' '}
                        {selectedVordruckEntry.recipient ? `→ ${selectedVordruckEntry.recipient}` : ''}
                      </div>
                    )}
                  </div>

                  <div className="p-2 border-r border-black col-span-1">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">
                      Vorrangstufe
                    </div>
                    {isVordruckEditing ? (
                      <select
                        value={editPriority}
                        onChange={e => setEditPriority(e.target.value as any)}
                        className="border border-black text-xs p-1 font-bold w-full mt-1 bg-white text-black"
                      >
                        <option value="NORMAL">Normal</option>
                        <option value="SOFORT">⚠️ Sofort</option>
                        <option value="BLITZ">⚡ Blitz</option>
                      </select>
                    ) : (
                      <div
                        className={`font-bold text-sm mt-1 ${
                          selectedVordruckEntry.priority === 'BLITZ'
                            ? 'text-rose-600'
                            : selectedVordruckEntry.priority === 'SOFORT'
                            ? 'text-amber-600'
                            : 'text-black'
                        }`}
                      >
                        {selectedVordruckEntry.priority === 'BLITZ'
                          ? '⚡ BLITZ'
                          : selectedVordruckEntry.priority === 'SOFORT'
                          ? '⚠️ SOFORT'
                          : 'NORMAL'}
                      </div>
                    )}
                  </div>

                  <div className="p-2 col-span-1">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">
                      Richtung
                    </div>
                    {isVordruckEditing ? (
                      <select
                        value={editDirection}
                        onChange={e => setEditDirection(e.target.value as any)}
                        className="border border-black text-xs p-1 font-bold w-full mt-1 bg-white text-black"
                      >
                        <option value="INCOMING">Eingang (▼)</option>
                        <option value="OUTGOING">Ausgang (▲)</option>
                        <option value="INTERNAL">Intern (■)</option>
                      </select>
                    ) : (
                      <div className="font-bold text-sm mt-1">
                        {selectedVordruckEntry.direction === 'INCOMING'
                          ? 'Eingang (▼)'
                          : selectedVordruckEntry.direction === 'OUTGOING'
                          ? 'Ausgang (▲)'
                          : 'Intern (■)'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Zeile 2: Datum | Uhrzeit | Aufgenommen von | Funkkanal */}
                <div className="grid grid-cols-4 border-b border-black text-xs">
                  <div className="p-2 border-r border-black">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">Datum / Tag</div>
                    <div className="font-bold mt-0.5">
                      {new Date(selectedVordruckEntry.timestamp).toLocaleDateString('de-DE')}
                    </div>
                  </div>
                  <div className="p-2 border-r border-black">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">Uhrzeit (Zeichen)</div>
                    <div className="font-bold mt-0.5">
                      {new Date(selectedVordruckEntry.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                    </div>
                  </div>
                  <div className="p-2 border-r border-black">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">Aufgenommen / Abgesetzt von</div>
                    <div className="font-bold mt-0.5 truncate">
                      {selectedVordruckEntry.created_by_name || 'Leitstelle / Stab'}
                    </div>
                  </div>
                  <div className="p-2">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">Funkkanal / Betriebsstelle</div>
                    <div className="font-bold mt-0.5">
                      {isVordruckEditing ? editChannel : selectedVordruckEntry.channel}
                    </div>
                  </div>
                </div>

                {/* Zeile 3: Empfänger (AN) & Absender (VON) */}
                <div className="grid grid-cols-2 border-b-2 border-black">
                  <div className="p-2 border-r border-black">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">Empfänger (An)</div>
                    {isVordruckEditing ? (
                      <input
                        type="text"
                        value={editRecipient}
                        onChange={e => setEditRecipient(e.target.value)}
                        placeholder="Empfänger eingeben..."
                        className="border border-black text-sm p-1 w-full font-bold mt-1 bg-white text-black"
                      />
                    ) : (
                      <div className="font-bold text-base mt-1">
                        {selectedVordruckEntry.recipient || '—'}
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">Absender (Von)</div>
                    {isVordruckEditing ? (
                      <input
                        type="text"
                        value={editSender}
                        onChange={e => setEditSender(e.target.value)}
                        placeholder="Absender eingeben..."
                        className="border border-black text-sm p-1 w-full font-bold mt-1 bg-white text-black"
                      />
                    ) : (
                      <div className="font-bold text-base mt-1">
                        {selectedVordruckEntry.sender || '—'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Zeile 4: Betreff & Einsatzabschnitt */}
                <div className="grid grid-cols-3 border-b border-black">
                  <div className="p-2 border-r border-black col-span-2">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">
                      Betreff / Stichwort
                    </div>
                    {isVordruckEditing ? (
                      <input
                        type="text"
                        value={editSubject}
                        onChange={e => setEditSubject(e.target.value)}
                        placeholder="Betreff / Stichwort..."
                        className="border border-black text-sm p-1 w-full font-bold mt-1 bg-white text-black"
                      />
                    ) : (
                      <div className="font-bold text-sm mt-0.5 text-blue-900">
                        {selectedVordruckEntry.subject || '—'}
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="text-[9px] text-gray-600 uppercase font-semibold">
                      Stabs- / Einsatzabschnitt
                    </div>
                    <div className="font-mono font-bold text-sm mt-0.5 text-indigo-700">
                      {isVordruckEditing ? editSection : (selectedVordruckEntry.section || '—')}
                    </div>
                  </div>
                </div>

                {/* Zeile 5: Text der Nachricht */}
                <div className="p-3 border-b-2 border-black min-h-[180px]">
                  <div className="text-[9px] text-gray-600 uppercase font-semibold mb-1">
                    Wortlaut der Nachricht / Anweisung
                  </div>
                  {isVordruckEditing ? (
                    <textarea
                      rows={6}
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                      className="w-full border border-black p-2 text-sm font-sans leading-relaxed resize-vertical bg-white text-black"
                    />
                  ) : (
                    <div className="text-sm font-medium whitespace-pre-wrap leading-relaxed">
                      {selectedVordruckEntry.content}
                    </div>
                  )}
                </div>

                {/* Zeile 6: Sichtvermerke der Sachgebiete S1-S6 & Leitung */}
                <div className="grid grid-cols-7 border-b border-black text-center bg-black/5">
                  {['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'Leitung'].map((sg, idx) => {
                    const curSec = isVordruckEditing ? editSection : selectedVordruckEntry.section;
                    const isAssigned = (sg === 'Leitung' && curSec === 'GESAMTLEITUNG') || curSec === sg;
                    return (
                      <div
                        key={sg}
                        onClick={() => {
                          if (isVordruckEditing) {
                            setEditSection(sg === 'Leitung' ? 'GESAMTLEITUNG' : sg);
                          }
                        }}
                        className={`p-1 ${idx < 6 ? 'border-r border-black' : ''} ${
                          isVordruckEditing ? 'cursor-pointer hover:bg-blue-100' : ''
                        } ${isAssigned ? 'bg-blue-200/80' : ''}`}
                        title={isVordruckEditing ? `Zuweisen an ${sg}` : undefined}
                      >
                        <div className="text-[9px] font-bold">{sg}</div>
                        <div className="h-6 flex items-center justify-center font-bold text-blue-700 text-base">
                          {isAssigned ? '✓' : isVordruckEditing ? <span className="opacity-25 text-xs">+</span> : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Zeile 7: Vermerk / Anweisung & Unterschrift */}
                <div className="grid grid-cols-3 p-2 border-b border-black">
                  <div className="col-span-2 pr-3">
                    <div className="text-[9px] text-gray-600 font-bold uppercase">Vermerk / Anweisung der Führung:</div>
                    {isVordruckEditing ? (
                      <textarea
                        rows={2}
                        value={editVermerk}
                        onChange={e => setEditVermerk(e.target.value)}
                        placeholder="Führungsanweisung, Weiterleitungsvermerk, Erledigungsvermerk..."
                        className="w-full border border-black p-1 text-xs resize-vertical mt-1 bg-white text-black"
                      />
                    ) : (
                      <div className="text-xs mt-1 italic text-gray-800 min-h-[24px]">
                        {selectedVordruckEntry.metadata?.vermerk || 'Kein Führungsvermerk hinterlegt.'}
                      </div>
                    )}
                    <div className="text-[10px] text-gray-600 mt-1">
                      Status: {isVordruckEditing ? (
                        <select
                          value={editStatus}
                          onChange={e => setEditStatus(e.target.value)}
                          className="border border-black text-[10px] p-0.5 font-bold ml-1 bg-white text-black"
                        >
                          <option value="ERFASST">ERFASST</option>
                          <option value="IN_BEARBEITUNG">IN_BEARBEITUNG</option>
                          <option value="ERLEDIGT">ERLEDIGT</option>
                          <option value="WEITERGELEITET">WEITERGELEITET</option>
                        </select>
                      ) : (
                        <span className={`font-bold ml-1 ${
                          selectedVordruckEntry.status === 'ERLEDIGT'
                            ? 'text-emerald-700'
                            : selectedVordruckEntry.status === 'IN_BEARBEITUNG'
                            ? 'text-amber-700'
                            : 'text-gray-800'
                        }`}>
                          {selectedVordruckEntry.status}
                          {selectedVordruckEntry.handled_by_name ? ` (erledigt durch: ${selectedVordruckEntry.handled_by_name})` : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-1 text-right">
                    <div className="text-[9px] text-gray-600 uppercase">Unterschrift / Handzeichen:</div>
                    <div className="border-b border-dotted border-black w-36 inline-block mt-6" />
                  </div>
                </div>

                {/* Kommentare & Lageverlauf */}
                <div className="p-3 bg-slate-50 border-t border-black">
                  <div className="text-xs font-bold text-slate-800 uppercase mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="w-3.5 h-3.5 text-blue-600" />
                      Kommentare & Lageverlauf
                    </span>
                    <span className="text-[10px] text-slate-500 font-normal">
                      {Array.isArray(selectedVordruckEntry.metadata?.comments) ? `${selectedVordruckEntry.metadata.comments.length} Einträge` : '0 Einträge'}
                    </span>
                  </div>

                  {Array.isArray(selectedVordruckEntry.metadata?.comments) && selectedVordruckEntry.metadata.comments.length > 0 ? (
                    <div className="flex flex-col gap-1.5 max-h-36 overflow-y-auto mb-2">
                      {selectedVordruckEntry.metadata.comments.map((c: any) => (
                        <div key={c.id} className="bg-white border border-slate-200 rounded p-2 text-xs">
                          <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                            <span className="font-bold text-blue-700">{c.author}</span>
                            <span>{new Date(c.created_at).toLocaleString('de-DE')} Uhr</span>
                          </div>
                          <div className="text-slate-800 whitespace-pre-wrap">{c.text}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400 italic mb-2">
                      Noch keine Kommentare erfasst.
                    </div>
                  )}

                  {/* Neuer Kommentar Input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCommentText}
                      onChange={e => setNewCommentText(e.target.value)}
                      placeholder="Kommentar / Ergänzung zum Vordruck eingeben..."
                      className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-xs bg-white text-black"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddComment(selectedVordruckEntry.id);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleAddComment(selectedVordruckEntry.id)}
                      disabled={commentSubmitting || !newCommentText.trim()}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-bold transition disabled:opacity-50"
                    >
                      {commentSubmitting ? 'Wird gespeichert…' : 'Kommentar speichern'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 bg-slate-900 border-t border-slate-800 flex justify-end">
              <button
                type="button"
                onClick={() => setShowVordruckModal(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold rounded-xl transition"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
